import {
  FinanceInvestigationFlow,
  financeInvestigationFlow,
  type FinanceInvestigationEvent,
} from "@ibada/shared-types/finance"

import { FinanceTransactionKind, FinanceTransactionStatus } from "@/features/finance/model/finance-types"

/** One day of the Explorer list: its `YYYY-MM-DD` key in the workspace zone and the events on it, in list order. */
export type InvestigationDayGroup = {
  dayKey: string
  /** Any instant of that day, for formatting the heading. */
  sample: string
  events: FinanceInvestigationEvent[]
}

/** The calendar day of an instant in `timeZone`, as `YYYY-MM-DD`. Falls back to the viewer's zone on a bad zone. */
export function zonedDayKey(value: string | Date, timeZone: string): string {
  const date = value instanceof Date ? value : new Date(value)
  try {
    return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).format(date)
  } catch {
    return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(date)
  }
}

/**
 * Groups the page's events by the day they happened in the workspace's zone, keeping the server's order (newest
 * first by default) both between days and inside each one. Events are never re-sorted here: the order is the
 * server's, so a page reads exactly as it was queried.
 */
export function groupInvestigationEventsByDay(
  events: readonly FinanceInvestigationEvent[],
  timeZone: string,
): InvestigationDayGroup[] {
  const groups: InvestigationDayGroup[] = []
  const byKey = new Map<string, InvestigationDayGroup>()
  for (const event of events) {
    const dayKey = zonedDayKey(event.occurredAt, timeZone)
    let group = byKey.get(dayKey)
    if (!group) {
      group = { dayKey, sample: event.occurredAt, events: [] }
      byKey.set(dayKey, group)
      groups.push(group)
    }
    group.events.push(event)
  }
  return groups
}

/** True when the event was corrected or voided, so its amount reads struck through. */
export function isCrossedOutInvestigationEvent(event: FinanceInvestigationEvent): boolean {
  return (
    event.status === FinanceTransactionStatus.Voided ||
    event.status === FinanceTransactionStatus.Cancelled ||
    event.isReversed ||
    event.kind === FinanceTransactionKind.Reversal
  )
}

/** The sign to print before an amount: "+" for money in, "-" for money out, none for an internal transfer. */
export function investigationAmountSign(flow: FinanceInvestigationFlow): string {
  if (flow === FinanceInvestigationFlow.In) return "+"
  if (flow === FinanceInvestigationFlow.Out) return "-"
  return ""
}

/** An event's amount with its sign, formatted by the caller's locale-aware money formatter. */
export function formatSignedInvestigationAmount(
  event: Pick<FinanceInvestigationEvent, "type" | "amount">,
  formatMoney: (amount: number) => string,
): string {
  return `${investigationAmountSign(financeInvestigationFlow(event.type))}${formatMoney(event.amount)}`
}

/** A signed total ("+71 994,02 $", "-20 416,00 $", "0,00 $"), the sign read from the value. */
export function formatSignedTotal(amount: number, formatMoney: (amount: number) => string): string {
  if (amount > 0) return `+${formatMoney(amount)}`
  if (amount < 0) return `-${formatMoney(Math.abs(amount))}`
  return formatMoney(0)
}

/** The time of day of an instant in `timeZone` ("02:00"), without a zone label: the page is in one zone. */
export function formatZonedTime(value: string, timeZone: string, locale: string): string {
  const date = new Date(value)
  const options: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date)
  } catch {
    return new Intl.DateTimeFormat(locale, options).format(date)
  }
}

/** A day heading in sentence case ("Mardi 28 juillet 2026", "Tuesday, July 28, 2026"). */
export function formatZonedDayHeading(value: string, timeZone: string, locale: string): string {
  const options: Intl.DateTimeFormatOptions = { weekday: "long", day: "numeric", month: "long", year: "numeric" }
  let text: string
  try {
    text = new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(new Date(value))
  } catch {
    text = new Intl.DateTimeFormat(locale, options).format(new Date(value))
  }
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1)
}

/** A resolved name for an id facet's chip, with the avatar when it names a person. */
export type ExplorerChipName = { name: string; avatarUrl?: string | null }

/** What the events themselves say an id is called, for the facets an event carries. */
function nameFromEvents(key: string, id: string, events: readonly FinanceInvestigationEvent[]): ExplorerChipName | null {
  for (const event of events) {
    if (key === "actorUserId" && event.actorUserId === id && event.actorName) {
      return { name: event.actorName, avatarUrl: event.actorAvatarUrl }
    }
    if (key === "fundId" && event.fundId === id && event.fundName) return { name: event.fundName }
    if (key === "activityId" && event.activityId === id && event.activityTitle) return { name: event.activityTitle }
    if (key === "accountingAccountId") {
      const account = event.accounts.find((candidate) => candidate.accountingAccountId === id)
      if (account) return { name: account.name }
    }
  }
  return null
}

/**
 * Names every active id facet's chip. In order: what the viewer just picked in a pill, what the results say the
 * record is called, then the URL's single `label`, and finally the raw id. The `label` has always named the
 * leading facet the link was built for, so it goes to the first facet the viewer did NOT pick here: picking a
 * second facet in a pill never steals the name a shared link carried for the first.
 */
export function resolveExplorerChipNames(input: {
  /** Active id facets in display order: `[urlKey, id]`. */
  facets: ReadonlyArray<readonly [string, string]>
  events: readonly FinanceInvestigationEvent[]
  /** Names picked in this session, keyed `${urlKey}:${id}`. */
  picked: Readonly<Record<string, string>>
  labelParam?: string
}): Record<string, ExplorerChipName> {
  const names: Record<string, ExplorerChipName> = {}
  let labelOwnerSeen = false
  for (const [key, id] of input.facets) {
    const picked = input.picked[`${key}:${id}`]
    const fromEvents = nameFromEvents(key, id, input.events)
    const ownsLabel = !picked && !labelOwnerSeen
    if (!picked) labelOwnerSeen = true
    const fallback = picked ?? (ownsLabel && input.labelParam ? input.labelParam : id)
    names[key] = fromEvents ?? { name: fallback }
  }
  return names
}
